// @flow

import * as React from 'react'
import {
  Typography,
  Grid,
  CircularProgress,
  TableContainer,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Paper,
} from '@material-ui/core'
import ErrorIcon from '@material-ui/icons/Error'
import SentimentVeryDissatisfiedIcon from '@material-ui/icons/SentimentVeryDissatisfied'

import { searchContentItems, getContentType } from 'services/cp-hcms'
import useIsMounted from 'hooks/useIsMounted'

import SubmissionHistoryResult from './SubmissionHistoryResult'

import NoData from './components/NoData'
const limit = 50

type Props = {
  integration: IntegrationCPHCMS,
  form: Form,
  startDate: string,
  endDate: string,
}

function SubmissionHistoryContainer({
  form,
  integration,
  startDate,
  endDate,
}: Props) {
  const isMounted = useIsMounted()

  const [
    {
      contentItems,
      nextOffset,
      hasMore,
      isFetchingContentItems,
      contentItemsError,
    },
    setContentItemsState,
  ] = React.useState({
    contentItems: [],
    nextOffset: 0,
    hasMore: false,
    isFetchingContentItems: true,
    contentItemsError: null,
  })

  const [
    { contentType, isFetchingContentType, contentTypeError },
    setContentTypeState,
  ] = React.useState({
    contentType: null,
    isFetchingContentType: true,
    contentTypeError: null,
  })

  const fetchContentItems = React.useCallback(
    async (offset) => {
      console.log('Fetching content items, offset:', offset)
      setContentItemsState((currentState) => ({
        ...currentState,
        contentItems: offset === 0 ? [] : currentState.contentItems,
        isFetchingContentItems: true,
        contentItemsError: null,
        hasFetched: false,
      }))

      let response = null
      let newError = null

      try {
        response = await searchContentItems({
          form,
          integration,
          offset,
          limit,
          startDate,
          endDate,
        })
      } catch (e) {
        newError = e
      }

      if (isMounted.current) {
        setContentItemsState((currentState) => {
          if (response) {
            const newContentItems = [
              ...currentState.contentItems,
              ...response.contentItems,
            ]
            return {
              isFetchingContentItems: false,
              contentItemsError: null,
              contentItems: newContentItems,
              nextOffset: response.nextOffset,
              hasMore: newContentItems.length < response.total,
            }
          }

          return {
            isFetchingContentItems: false,
            contentItemsError: newError,
            contentItems: [],
            nextOffset: 0,
            hasMore: false,
          }
        })
      }
    },
    [form, integration, isMounted, startDate, endDate]
  )

  React.useEffect(() => {
    fetchContentItems(0)

    setContentTypeState({
      isFetchingContentType: true,
      contentType: null,
      contentTypeError: null,
    })

    let ignore = false
    const fetchContentType = async () => {
      let newContentType = null
      let newContentTypeError = null

      try {
        newContentType = await getContentType({
          form,
          integration,
        })
      } catch (e) {
        newContentTypeError = e
      }

      if (!ignore) {
        setContentTypeState({
          isFetchingContentType: false,
          contentType: newContentType,
          contentTypeError: newContentTypeError,
        })
      }
    }
    fetchContentType()

    return () => {
      ignore = true
    }
  }, [fetchContentItems, form, integration, startDate, endDate])

  React.useEffect(() => {
    const scrollEventListener = () => {
      // Bails early if we have not fetched data yet and:
      // - there's an error
      // - it's already loading
      // - there's nothing left to load
      if (
        !document.body ||
        contentItemsError ||
        isFetchingContentItems ||
        !hasMore
      ) {
        return
      }

      // Checks that the page has scrolled to the bottom
      if (window.innerHeight + window.scrollY >= document.body.scrollHeight) {
        fetchContentItems(nextOffset)
      }
    }
    // Binds our scroll event handler
    window.addEventListener('scroll', scrollEventListener)

    return () => {
      window.removeEventListener('scroll', scrollEventListener)
    }
  }, [
    contentItemsError,
    fetchContentItems,
    hasMore,
    isFetchingContentItems,
    nextOffset,
  ])

  if (contentTypeError) {
    return (
      <>
        <Typography align="center" variant="h2" color="error">
          <ErrorIcon fontSize="inherit" color="inherit" />
        </Typography>
        <Typography align="center" color="error">
          {contentTypeError.message}
        </Typography>
      </>
    )
  }

  if (isFetchingContentType) {
    return (
      <Grid container justify="center">
        <CircularProgress />
      </Grid>
    )
  }

  if (!contentType) {
    return (
      <>
        <Typography align="center" variant="h2" color="textSecondary">
          <SentimentVeryDissatisfiedIcon fontSize="inherit" color="inherit" />
        </Typography>
        <Typography align="center" color="textSecondary" gutterBottom>
          Could find the content type for form: <i>{form.name}</i>. The content
          type is not created until a user has submitted this form. Please wait
          until a user has completed a form submission successfully.
        </Typography>
      </>
    )
  }

  return (
    <>
      <SubmissionHistoryResults
        integration={integration}
        contentItems={contentItems}
        contentType={contentType}
      />
      <SubmissionHistoryResultsFooter
        error={contentItemsError}
        isFetching={isFetchingContentItems}
        hasMore={hasMore}
        contentItems={contentItems}
      />
    </>
  )
}

const SubmissionHistoryResults = React.memo(
  ({ integration, contentItems, contentType }) => {
    if (!contentItems.length) {
      return null
    }

    return (
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>CREATED</TableCell>
              <TableCell>BY</TableCell>
              <TableCell>STATUS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contentItems.map((contentItem) => (
              <SubmissionHistoryResult
                key={contentItem.id}
                integration={integration}
                contentItem={contentItem}
                contentType={contentType}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }
)

const SubmissionHistoryResultsFooter = React.memo(
  ({ error, isFetching, hasMore, contentItems }) => {
    if (error) {
      return (
        <>
          <Typography align="center" variant="h2" color="error">
            <ErrorIcon fontSize="inherit" color="inherit" />
          </Typography>
          <Typography align="center" color="error">
            {error.message}
          </Typography>
        </>
      )
    }

    if (isFetching) {
      return (
        <Grid container justify="center">
          <CircularProgress />
        </Grid>
      )
    }

    if (!contentItems.length) {
      return (
        <>
          <Typography align="center" variant="h2" color="textSecondary">
            <SentimentVeryDissatisfiedIcon fontSize="inherit" color="inherit" />
          </Typography>
          <Typography align="center" color="textSecondary" gutterBottom>
            Could find any submissions for the given search parameters. Try
            changing your search parameters.
          </Typography>
        </>
      )
    }

    if (!hasMore) {
      return (
        <NoData
          variant="info"
          text="There are no more submissions for the given search parameters."
        />
      )
    }

    return null
  }
)

export default React.memo<Props>(SubmissionHistoryContainer)
